import { useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Download, Printer, FileText } from "lucide-react";
import { toast } from "sonner";
import { buildInvoicePdf, InvoiceItem as Item } from "@/lib/invoice";

const todayStr = () => new Date().toISOString().slice(0, 10);
const genInvoiceNo = () => {
  const d = new Date();
  const y = d.getFullYear().toString().slice(-2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const rand = Math.floor(Math.random() * 9000 + 1000);
  return `RUP-${y}${m}${day}-${rand}`;
};

const fmt = (n: number) => `BDT ${n.toLocaleString("en-BD", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminInvoice = () => {
  const [invoiceNo, setInvoiceNo] = useState(genInvoiceNo());
  const [date, setDate] = useState(todayStr());
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "", address: "" });
  const [items, setItems] = useState<Item[]>([{ name: "", qty: 1, price: 0 }]);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState("Thank you for your purchase!");
  const previewRef = useRef<HTMLDivElement>(null);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);
  const grandTotal = Math.max(0, subtotal - discount + tax);

  const updateItem = (idx: number, patch: Partial<Item>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((p) => [...p, { name: "", qty: 1, price: 0 }]);
  const removeItem = (idx: number) =>
    setItems((p) => (p.length > 1 ? p.filter((_, i) => i !== idx) : p));

  const buildPdf = () =>
    buildInvoicePdf({
      invoiceNo,
      date,
      customer,
      items,
      discount,
      tax,
      notes,
    });

  const downloadPdf = async () => {
    try {
      const doc = await buildPdf();
      doc.save(`${invoiceNo}.pdf`);
      toast.success("Invoice downloaded");
    } catch (e: any) {
      toast.error(e.message || "Failed to generate PDF");
    }
  };

  const printPdf = async () => {
    try {
      const doc = await buildPdf();
      doc.autoPrint();
      const url = doc.output("bloburl");
      const w = window.open(url, "_blank");
      if (!w) toast.error("Allow pop-ups to print");
    } catch (e: any) {
      toast.error(e.message || "Failed to print");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Invoice Generator</h1>
          <p className="text-sm text-muted-foreground">Create and download a professional A4 invoice</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={printPdf} className="gap-2">
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button onClick={downloadPdf} className="gap-2">
            <Download className="h-4 w-4" /> Download PDF
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="p-5 sm:p-6 border-0 shadow-soft space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Invoice No</Label>
              <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Customer</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
              <Input placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
              <Input placeholder="Email" type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="sm:col-span-2" />
              <Textarea placeholder="Address" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className="sm:col-span-2" rows={2} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Items</h3>
              <Button size="sm" variant="outline" onClick={addItem} className="h-8 gap-1">
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-6" placeholder="Product name" value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} />
                  <Input className="col-span-2" type="number" min={1} value={it.qty} onChange={(e) => updateItem(idx, { qty: Number(e.target.value) || 0 })} />
                  <Input className="col-span-3" type="number" min={0} step="0.01" value={it.price} onChange={(e) => updateItem(idx, { price: Number(e.target.value) || 0 })} />
                  <Button size="icon" variant="ghost" className="col-span-1 h-9 w-9" onClick={() => removeItem(idx)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Discount (BDT)</Label>
              <Input type="number" min={0} value={discount} onChange={(e) => setDiscount(Number(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Tax (BDT)</Label>
              <Input type="number" min={0} value={tax} onChange={(e) => setTax(Number(e.target.value) || 0)} />
            </div>
          </div>

          <div>
            <Label>Footer note</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </Card>

        {/* Preview */}
        <Card className="p-4 sm:p-6 border-0 shadow-soft bg-muted/40">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" /> A4 Preview
          </div>
          <div
            ref={previewRef}
            className="bg-white text-foreground mx-auto shadow-hover overflow-hidden"
            style={{ aspectRatio: "210 / 297", width: "100%", maxWidth: 560, padding: "6%" }}
          >
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-display text-2xl font-bold text-primary">Rupshoili</h2>
                <p className="text-[10px] text-muted-foreground mt-1">Honest products, honest prices.</p>
                <p className="text-[10px] text-muted-foreground">Dhaka, Bangladesh</p>
                <p className="text-[10px] text-muted-foreground">support@rupshoili.com</p>
              </div>
              <div className="text-right">
                <h3 className="font-bold text-lg">INVOICE</h3>
                <p className="text-[10px] text-muted-foreground mt-1">#{invoiceNo}</p>
                <p className="text-[10px] text-muted-foreground">{date}</p>
              </div>
            </div>
            <div className="border-t-2 border-primary my-3" />

            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider">Bill To</p>
              <div className="text-[11px] text-muted-foreground mt-1 space-y-0.5">
                <p className="text-foreground font-medium">{customer.name || "—"}</p>
                {customer.phone && <p>Phone: {customer.phone}</p>}
                {customer.email && <p>Email: {customer.email}</p>}
                {customer.address && <p>{customer.address}</p>}
              </div>
            </div>

            <table className="w-full mt-4 text-[10px]">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="p-1.5 text-left">#</th>
                  <th className="p-1.5 text-left">Product</th>
                  <th className="p-1.5 text-center">Qty</th>
                  <th className="p-1.5 text-right">Price</th>
                  <th className="p-1.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-1.5">{i + 1}</td>
                    <td className="p-1.5">{it.name || "—"}</td>
                    <td className="p-1.5 text-center">{it.qty}</td>
                    <td className="p-1.5 text-right">{fmt(it.price)}</td>
                    <td className="p-1.5 text-right">{fmt(it.qty * it.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end mt-4">
              <div className="w-1/2 text-[10px] space-y-1">
                <div className="flex justify-between"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                <div className="flex justify-between"><span>Discount</span><span>- {fmt(discount)}</span></div>
                <div className="flex justify-between"><span>Tax</span><span>{fmt(tax)}</span></div>
                <div className="flex justify-between bg-primary text-primary-foreground px-2 py-1.5 mt-1 font-bold">
                  <span>GRAND TOTAL</span><span>{fmt(grandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-6">
              <div className="border-t pt-2 flex justify-between items-end text-[10px] text-muted-foreground">
                <p className="italic">{notes}</p>
                <div className="text-center">
                  <div className="border-t border-foreground/40 w-32 mb-1" />
                  <p>Authorized Signature</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminInvoice;
